import React from 'react';
import cx from 'classnames';
import { ga4Track, toggleInteraction } from '@unisporkal/ga4';
import { useTranslations } from '@unisporkal/localization';
import { useTheme } from '@unisporkal/themes';
import type { FilmSize as FilmSizeShape } from '../../../../types/sizeType';
import acquisitionTypes from '../../../../support/acquisitionTypes';
import PriceInfo from '../PriceInfo/PriceInfo';
import styles from '../AssetSizes.module.scss';
import useSelectedIndex from '../../../../hooks/stateful/useSelectedIndex';
import { useAcquisitionType } from '../../../../hooks/stateful/useAcquisitionType';
import { useAsset } from '../../../../hooks/stateful/useAsset';

interface FilmSizeProps {
  size: FilmSizeShape;
  index: number;
  singleView?: boolean;
}

interface FilmSizeItem {
  license?: string;
  bytes?: number;
}

const FilmSize = ({ size, index, singleView = false }: FilmSizeProps) => {
  const t = useTranslations();
  const theme = useTheme();
  const themeRadio = theme.radio || {};
  const { acquisitionType } = useAcquisitionType();
  const { asset } = useAsset();
  const { selectedOption, selectedSizeIndex, setSelectedSizeIndex } =
    useSelectedIndex();

  const showPixelsLabel =
    acquisitionType === acquisitionTypes.LICENSE ||
    acquisitionType === acquisitionTypes.DOWNLOAD ||
    acquisitionType === acquisitionTypes.CART_ADP_CONFIRMATION;
  const isSelected = selectedSizeIndex === index;

  const trackClick = () => {
    const ga4Event = toggleInteraction({
      toggle_interaction: 'rf_toggle',
      toggle_selection: `${size.label.toLowerCase()}_size_video`,
      ui_element_location: 'acquisition_card_options',
    });

    ga4Track(ga4Event);
  };

  const handleClick = () => {
    setSelectedSizeIndex(index);
    trackClick();

    if (window.tracking_data?.asset_details) {
      window.tracking_data.asset_details.asset_size = `${size.definition}_${size.codec}`;
    }
  };

  const ifAnyFilmSizeByteZero = (): boolean => {
    const videoBytes: number[] = [];
    if (asset.isFilm()) {
      selectedOption?.sizes?.forEach((filmSize: FilmSizeItem) => {
        if (!filmSize.license) {
          videoBytes.push(filmSize.bytes || 0);
        }
      });
    }
    return videoBytes.includes(0);
  };

  const inputSection = () =>
    !singleView && (
      <input
        className={cx(themeRadio.radioButton, styles.radio)}
        type="radio"
        checked={isSelected}
        onChange={handleClick}
        id={size.label}
      />
    );

  const infoSection = () =>
    (isSelected || singleView) && (
      <div className={styles.sizeDetails}>
        <div className={styles.info}>
          {showPixelsLabel
            ? t('img_size_in_pixels', {
                width: size.pixelWidth,
                height: size.pixelHeight,
              })
            : size.pixels}
          <div className={styles.seperator}>|</div>
          {t('frames_per_second', { frame_rate: size.frameRate })}
          <div className={styles.seperator}>|</div>
          {size.contentType}
          {!ifAnyFilmSizeByteZero() && (
            <span>
              <div className={styles.seperator}>|</div>
              {size.fileSize}
            </span>
          )}
        </div>
      </div>
    );

  const sizeLabelStyleName = (): string => {
    if (singleView) {
      return styles.singleSizeItem;
    }
    if (isSelected) {
      return styles.selectedSizeItem;
    }
    return styles.sizeItem;
  };

  return (
    <label
      className={cx(themeRadio.radio, sizeLabelStyleName())}
      data-testid="asset-size"
    >
      <div
        className={styles.itemDetails}
        data-testid="film-size"
      >
        {inputSection()}
        <div className={styles.description}>
          <span className={styles.name}>
            <span className={styles.assetSizeLabel}>{size.label}</span>
            <span className={styles.codec}>{size.codec}</span>
          </span>
          {infoSection()}
        </div>
      </div>
      <PriceInfo
        size={size}
        currencyCode={size.currencyCode}
      />
    </label>
  );
};

export default FilmSize;
